import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReloadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import ScoreCard from './ScoreCard';
import UpdateScoreModal from './UpdateScoreModal';
import {
  declareWinnerRequest,
  directUpdateTournamentparticipantScoreRequest,
  getTournamentMatchScoreRequest,
  getTournamentBracketRequest,
} from '../../../api/requests/tournaments';
import { useEventContext } from '../../../contexts/EventContext';
import { ParticipantSide } from '../../../enums/enums';
import { LoadingContainer } from './Steps/Step2Sports';
import Spinner from '../../common/Spinner';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import CustomButton from '../../common/Button';
import {
  DeclareTournamentMatchWinnerPayload,
  SetScore,
  TournamentMatchScoreValue,
  UpdateScorePayloadType,
} from '../../../types/types';
import { buildScoreJsonFromSets } from '../../../utils/eventUtils';
import { Switch } from 'antd';

const MatchScoreScreen: React.FC = () => {
  const { matchId } = useParams();
  const { tournamentType, sportName: contextSportName } = useEventContext();
  const tournamentMatchId = Number(matchId);
  const snackbar = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matchScore, setMatchScore] =
    useState<TournamentMatchScoreValue | null>(null);

  // Prefer the sport name from the match-score response (reliable on refresh / direct nav);
  // fall back to the event context only if the API didn't provide one.
  const sportName = matchScore?.sportName || contextSportName;
  const [p1ScoreData, setP1ScoreData] = useState<string | null>(null);
  const [p2ScoreData, setP2ScoreData] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isMatchByeRound, setIsMatchByeRound] = useState<boolean>(false);
  // Scoring is blocked while the bracket is stale or registration is still open.
  const [scoringBlocked, setScoringBlocked] = useState<boolean>(false);

  const fetchMatchScore = async () => {
    try {
      setIsLoading(true);
      const response = await getTournamentMatchScoreRequest(tournamentMatchId);
      response && response.value && setMatchScore(response.value);
    } catch (error) {
      console.error('Error fetching match score:', error);
      snackbar.error('Failed to fetch match score');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine whether scoring is blocked (stale bracket or open registration) for this match's
  // tournament. The API enforces this too; this disables the controls and warns first.
  const fetchScoringBlocked = async (tournamentId: number) => {
    try {
      const response = await getTournamentBracketRequest(tournamentId);
      const value = response?.value;
      setScoringBlocked(
        Boolean(value?.bracketIsStale) || Boolean(value?.registrationWindowOpen),
      );
    } catch {
      // On failure leave controls enabled; the API still enforces the rule.
      setScoringBlocked(false);
    }
  };

  const declareWinner = async ({ winnerId }: { winnerId: number }) => {
    try {
      const declareWinnerPayload: DeclareTournamentMatchWinnerPayload = {
        tournamentMatchId: tournamentMatchId,
        autoAdvanceWinner: true,
        isMatchByeRound: isMatchByeRound,
        winnerId: winnerId,
        participant1Score: matchScore?.participant1?.totalScore ?? 0,
        participant1ScoreData:
          p1ScoreData ?? matchScore?.participant1?.scoreData,
        participant2Score: matchScore?.participant2?.totalScore ?? 0,
        participant2ScoreData:
          p2ScoreData ?? matchScore?.participant2?.scoreData,
        matchNotes: '',
        refereeNotes: '',
      };
      const response = await declareWinnerRequest(
        Number(matchScore?.tournamentId),
        declareWinnerPayload,
      );
      if (response?.isSuccess) {
        snackbar.success('Winner declared successfully');
        await fetchMatchScore();
      } else {
        snackbar.error(response?.error || 'Failed to declare winner');
      }
    } catch (error) {
      console.error('Error declaring winner:', error);
      snackbar.error('Failed to declare winner');
    } finally {
      setSaving(false);
    }
  };

  const updateScoreAsync = async ({
    jsonData,
    participantSide,
  }: {
    jsonData: string;
    participantSide: ParticipantSide;
  }) => {
    try {
      setSaving(true);
      const payload: UpdateScorePayloadType = {
        tournamentMatchId: tournamentMatchId,
        scoringParticipant: participantSide,
        scoreDataJson: jsonData,
      };
      const response =
        await directUpdateTournamentparticipantScoreRequest(payload);
      return response;
    } catch (error) {
      console.error('Error updating score:', error);
      snackbar.error('Failed to update score');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveScores = async (p1Sets: SetScore[], p2Sets: SetScore[]) => {
    try {
      const p1ScoreJsonData = buildScoreJsonFromSets(p1Sets, sportName);
      const p2ScoreJsonData = buildScoreJsonFromSets(p2Sets, sportName);

      const p1Resp = await updateScoreAsync({
        jsonData: p1ScoreJsonData,
        participantSide: ParticipantSide.Participant1,
      });

      if (!p1Resp?.isSuccess) {
        return snackbar.error(p1Resp?.error);
      }

      const p2Resp = await updateScoreAsync({
        jsonData: p2ScoreJsonData,
        participantSide: ParticipantSide.Participant2,
      });

      if (!p2Resp?.isSuccess) {
        return snackbar.error(p2Resp?.error);
      }
      snackbar.success('Set score updated successfully.');
      closeUpdateModal();
      await fetchMatchScore();
    } catch (err) {
      console.error('Error updating set score:', err);
      snackbar.error(
        err instanceof Error ? err.message : 'Failed to update set score',
      );
    }
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
  };

  useEffect(() => {
    fetchMatchScore();
  }, []);

  useEffect(() => {
    if (matchScore) {
      const isByeMatch = !(
        matchScore?.participant1 && matchScore?.participant2
      );
      if (isByeMatch) setIsMatchByeRound(isByeMatch);
      else setIsMatchByeRound(matchScore?.isByeMatch);

      if (matchScore?.tournamentId) {
        fetchScoringBlocked(matchScore.tournamentId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchScore]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Page>
      {matchScore === null ? (
        <>No data</>
      ) : (
        <>
          <TopActions>
            <ByeRoundContainer>
              <ByeMatchControlContent>
                <ByeMatchText>BYE Match</ByeMatchText>
                <Switch
                  value={isMatchByeRound}
                  onChange={setIsMatchByeRound}
                  disabled={
                    matchScore?.matchStatus === 'Completed' ||
                    matchScore?.matchStatus === 'Cancelled' ||
                    !matchScore?.participant1 ||
                    !matchScore?.participant2 ||
                    scoringBlocked
                  }
                />
              </ByeMatchControlContent>
              <ByeMatchControlContent>
                <ByeNoteLabel>Note:</ByeNoteLabel>
                <ByeNoteText>
                  Use BYE Match to declare a winner when only one player/team is
                  available, without entering scores.
                </ByeNoteText>
              </ByeMatchControlContent>
            </ByeRoundContainer>
            <CustomButton
              bgColor={
                matchScore.matchStatus === 'Completed' ||
                !matchScore?.participant1 ||
                !matchScore?.participant2 ||
                isMatchByeRound ||
                scoringBlocked
                  ? COLORS.grey.primary
                  : COLORS.blue.primary
              }
              textColor={
                matchScore.matchStatus === 'Completed' ||
                !matchScore?.participant1 ||
                !matchScore?.participant2 ||
                isMatchByeRound ||
                scoringBlocked
                  ? COLORS.grey.dark
                  : COLORS.white.primary
              }
              icon={<ReloadOutlined />}
              onClick={() => setShowUpdateModal(true)}
              disabled={
                matchScore.matchStatus === 'Completed' ||
                !matchScore?.participant1 ||
                !matchScore?.participant2 ||
                isMatchByeRound ||
                scoringBlocked
              }
            >
              Update Score
            </CustomButton>
          </TopActions>
          <CardsGrid>
            <ScoreCard
              participantDetails={matchScore.participant1}
              tournamentType={tournamentType}
              participantNumber={1}
              sportName={sportName}
              tournamentMatchId={Number(tournamentMatchId)}
              onDeclaration={declareWinner}
              onScoreChange={setP1ScoreData}
              isMatchCompleted={
                matchScore.matchStatus === 'Completed' ||
                matchScore.matchStatus === 'Cancelled'
              }
              disableDeclare={scoringBlocked}
              winnerId={matchScore.winnerId}
            />
            <ScoreCard
              participantDetails={matchScore.participant2}
              tournamentType={tournamentType}
              participantNumber={2}
              sportName={sportName}
              tournamentMatchId={Number(tournamentMatchId)}
              onDeclaration={declareWinner}
              onScoreChange={setP2ScoreData}
              isMatchCompleted={
                matchScore.matchStatus === 'Completed' ||
                matchScore.matchStatus === 'Cancelled'
              }
              disableDeclare={scoringBlocked}
              winnerId={matchScore.winnerId}
            />
          </CardsGrid>
          <UpdateScoreModal
            visible={showUpdateModal}
            onClose={closeUpdateModal}
            participant1={matchScore.participant1}
            participant2={matchScore.participant2}
            sportName={sportName}
            tournamentType={tournamentType}
            disabled={saving}
            tournamentMatchId={Number(tournamentMatchId)}
            onSave={handleSaveScores}
          />
        </>
      )}
    </Page>
  );
};

export default MatchScoreScreen;

/* ---------------- styles ---------------- */

const Page = styled.div`
  padding: 24px;
`;

const TopActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const CardsGrid = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
`;

const ByeRoundContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ByeMatchControlContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
`;

const ByeMatchText = styled.p`
  font-size: ${TEXT_SIZES.MD}px;
  margin-bottom: 0px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const ByeNoteLabel = styled.p`
  margin-bottom: 0px;
`;

const ByeNoteText = styled.p`
  color: ${COLORS.grey.medium};
  margin-bottom: 0px;
`;
